/* eslint-disable @typescript-eslint/no-explicit-any */
import { IFirebaseAuthentication } from '@/modules/Firebase/firebase-types'
import {
  type FirebaseApp,
  initializeApp,
  getApps,
  getApp,
  deleteApp,
} from 'firebase/app'
import {
  Database,
  getDatabase,
  ref,
  onChildAdded,
  onValue,
  set,
  child,
  onChildChanged,
  onChildRemoved,
  off,
} from 'firebase/database'
import { Auth, getAuth, signInWithCustomToken, signOut } from 'firebase/auth'

import {
  IMMEDIATE_NODE,
  SCHEDULED_NODE,
  NOSHOW_NODE,
  ATTACHMENTS_NODE,
  STRATIFICATION_NODE,
} from '@/modules/Firebase/firebase-constants'
import { getFirebaseToken } from '@/modules/Firebase/services'
import jwtDecode from 'jwt-decode'
export default class Firebase {
  private _instance?: FirebaseApp | null
  private _scheduledReference: ReturnType<typeof ref>
  private _immediateReference: ReturnType<typeof ref>
  private _attachmentsReference: ReturnType<typeof ref>
  private _noShowReference: ReturnType<typeof ref>
  private _stratificationReference: ReturnType<typeof ref>

  private auth: Auth
  private database: Database
  private _hasImmediate: boolean

  constructor(
    private readonly connectData: IFirebaseAuthentication,
    private readonly firebaseUserToken: string,
    private readonly clinicId: number,
    private readonly patientId: number,
    private readonly onRefresh: (data: string) => void,
  ) {
    if (!getApps()?.length) {
      this._instance = initializeApp(this.connectData)
    } else {
      this._instance = getApp()
    }

    this.database = getDatabase(this._instance)
    this.auth = getAuth(this._instance)

    this.initializeObserver()

    const exp = jwtDecode<{ exp: number }>(firebaseUserToken).exp
    if (exp <= 0) this.refreshToken(exp, true)
    else this.authenticate(false)
  }

  get instance() {
    return this._instance
  }

  private authenticate(refreshing: boolean): void {
    signInWithCustomToken(this.auth, this.firebaseUserToken).catch(err => {
      console.error(err?.message)
    })
    if (!refreshing)
      this.refreshToken(
        jwtDecode<{ exp: number }>(this.firebaseUserToken).exp,
        false,
      )
  }

  private initializeObserver(): void {
    this._scheduledReference = ref(
      this.database,
      `filaAtendimento/${this.clinicId}/agendado/${this.patientId}`,
    )

    this._immediateReference = ref(
      this.database,
      `filaAtendimento/${this.clinicId}/imediato/${this.patientId}`,
    )
    this._noShowReference = ref(
      this.database,
      `/noshow/${this.clinicId}/${this.patientId}`,
    )
    this._attachmentsReference = ref(
      this.database,
      `/anexos/${this.clinicId}/${this.patientId}`,
    )
    this._stratificationReference = ref(
      this.database,
      `/estratificacao/${this.clinicId}/${this.patientId}`,
    )
  }

  private static handleValue(value, node?: string) {
    if (node === STRATIFICATION_NODE) {
      return {
        estratificacao: value.val(),
      }
    }
    if (node === NOSHOW_NODE) {
      return {
        noshow: value.val(),
      }
    }
    if (node === ATTACHMENTS_NODE) {
      return {
        anexos: value.val(),
      }
    }
    if (node === IMMEDIATE_NODE) {
      return { [value.key]: value.val() }
    }
    const attendanceCache = value.val()
    if (attendanceCache.filaAtendimento) {
      attendanceCache.filaAtendimento.idProtocolo = value.key
    }
    const attachments = attendanceCache.filaAtendimento?.anexos
    if (attachments) {
      const aux = []
      for (const attachment in attachments) {
        const translatedAttachment = {
          id: attachments[attachment].idAtendimentoAnexo,
          attachmentId: attachments[attachment].idAnexo,
          description: attachments[attachment].legenda,
          url: attachments[attachment].urlDownload,
          size: attachments[attachment].tamanho,
          status: 'UPLOADED',
          progress: 100,
          extension: attachments[attachment].nomeExtensao,
        }
        attachments[attachment] = translatedAttachment
        aux.push(attachments[attachment])
      }
      attendanceCache.filaAtendimento.anexos = aux
    }
    return attendanceCache
  }

  onAdd(fn: (data) => void, node: string): void {
    if (node === IMMEDIATE_NODE) {
      onChildAdded(this._immediateReference, d => {
        fn(Firebase.handleValue(d, IMMEDIATE_NODE))
        this._hasImmediate = true
      })
    }

    if (node === SCHEDULED_NODE) {
      onChildAdded(this._scheduledReference, d =>
        fn(Firebase.handleValue(d, SCHEDULED_NODE)),
      )
    }

    if (node === NOSHOW_NODE) {
      onValue(this._noShowReference, d =>
        fn(Firebase.handleValue(d, NOSHOW_NODE)),
      )
    }
    if (node === ATTACHMENTS_NODE) {
      onValue(this._attachmentsReference, d => {
        fn(Firebase.handleValue(d, ATTACHMENTS_NODE))
      })
    }
    if (node === STRATIFICATION_NODE) {
      onValue(this._stratificationReference, d =>
        fn(Firebase.handleValue(d, STRATIFICATION_NODE)),
      )
    }
  }

  patientOnline(online: boolean): void {
    if (this._instance) {
      const ref = child(
        this._immediateReference,
        'filaAtendimento/pacienteOnline',
      )

      set(ref, online)
    }
  }

  onChange(fn: (data) => void, node: string): void {
    if (node === IMMEDIATE_NODE) {
      onChildChanged(this._immediateReference, d =>
        fn(Firebase.handleValue(d, IMMEDIATE_NODE)),
      )
    }

    if (node === SCHEDULED_NODE) {
      onChildChanged(this._scheduledReference, d =>
        fn(Firebase.handleValue(d, SCHEDULED_NODE)),
      )
    }

    if (node === NOSHOW_NODE) {
      onValue(this._noShowReference, d =>
        fn(Firebase.handleValue(d, NOSHOW_NODE)),
      )
    }
    if (node === ATTACHMENTS_NODE) {
      onValue(this._attachmentsReference, d => {
        fn(Firebase.handleValue(d, ATTACHMENTS_NODE))
      })
    }

    if (node === STRATIFICATION_NODE) {
      onValue(this._stratificationReference, d =>
        fn(Firebase.handleValue(d, STRATIFICATION_NODE)),
      )
    }
  }

  onRemove(fn: (data) => void, node: string): void {
    if (node === IMMEDIATE_NODE) {
      onChildRemoved(this._immediateReference, d => {
        fn(Firebase.handleValue(d, IMMEDIATE_NODE))
        this._hasImmediate = false
      })
    }

    if (node === SCHEDULED_NODE) {
      onChildRemoved(this._scheduledReference, d =>
        fn(Firebase.handleValue(d, SCHEDULED_NODE)),
      )
    }

    if (node === ATTACHMENTS_NODE) {
      onChildRemoved(this._attachmentsReference, d =>
        fn(Firebase.handleValue(d, ATTACHMENTS_NODE)),
      )
    }

    if (node === STRATIFICATION_NODE) {
      onChildRemoved(this._stratificationReference, d => {
        fn(Firebase.handleValue(d, ATTACHMENTS_NODE))
      })
    }
  }

  private refreshToken(expire: number, initialize: boolean): void {
    let timeToRefresh = new Date(expire * 1000).getTime() - new Date().getTime()
    if (timeToRefresh <= 0) timeToRefresh = 500
    setTimeout(() => {
      getFirebaseToken().then(res => {
        signInWithCustomToken(this.auth, this.firebaseUserToken).catch(err => {
          console.error(err?.message)
        })

        if (initialize) this.authenticate(true)
        this.onRefresh(res)
      })
    }, timeToRefresh)
  }

  destroy(): Firebase {
    off(this._scheduledReference)
    off(this._immediateReference)
    off(this._noShowReference)
    off(this._stratificationReference)
    off(this._attachmentsReference)

    signOut(this.auth)

    if (this._instance) {
      deleteApp(this._instance)
    }

    this._instance = null
    return this
  }
}
